import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../api/errors';
import {
  ProjectMemberRole,
  ProjectTaskProgressMethod,
  projectMemberRoleNames,
  projectTaskProgressMethodNames,
} from '../api/projects';
import type { ProjectMemberRoleValue, ProjectTaskProgressMethodValue } from '../api/projects';
import {
  addProjectTemplateTask,
  getProjectTemplate,
  projectTemplateVariables,
  removeProjectTemplateTask,
  updateProjectTemplateTask,
} from '../api/projectTemplates';
import type {
  ProjectTemplateChecklistItem,
  ProjectTemplateDetail,
  ProjectTemplateTask,
} from '../api/projectTemplates';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';

/** Giá trị của biểu mẫu thêm và sửa một việc trong mẫu. */
interface TaskFormValues {
  readonly parentId: string | null;
  readonly sequenceNumber: number | null;
  readonly name: string;
  readonly defaultDurationDays: number | null;
  readonly defaultEffortHours: number | null;
  readonly defaultAssigneeRole: ProjectMemberRoleValue | null;
  readonly defaultProgressMethod: ProjectTaskProgressMethodValue | null;
  readonly isMilestone: boolean;
  readonly isOptional: boolean;
  readonly guidanceNote: string | null;
  readonly dependsOnSequenceNumbers: readonly number[] | null;
}

/** Một dòng của bảng: công việc kèm độ sâu để thụt lề. */
interface TaskRow {
  readonly task: ProjectTemplateTask;
  readonly depth: number;
}

/** Trọng số mặc định của một mục kiểm. Khớp với `ProjectTaskChecklistItem.DefaultWeight`. */
const defaultChecklistWeight = 1;

/** Bề rộng một cấp thụt lề, tính bằng điểm ảnh. */
const indentWidth = 18;

/**
 * Xếp cây công việc của mẫu thành danh sách phẳng theo thứ tự đọc.
 *
 * Sắp theo cây chứ không sắp theo số thứ tự: một việc con có số thứ tự lớn hơn việc gốc kế tiếp là
 * chuyện bình thường, và sắp phẳng theo số sẽ ném việc con ra khỏi nhóm cha của nó.
 *
 * Có tập đã đi qua để một quan hệ cha con vòng — nếu có bằng cách nào đó — không làm treo giao diện.
 */
function flattenTaskTree(tasks: readonly ProjectTemplateTask[]): readonly TaskRow[] {
  const childrenByParent = new Map<string | null, ProjectTemplateTask[]>();

  for (const task of tasks) {
    const key = task.parentId;
    const siblings = childrenByParent.get(key);

    if (siblings === undefined) {
      childrenByParent.set(key, [task]);
    } else {
      siblings.push(task);
    }
  }

  for (const siblings of childrenByParent.values()) {
    siblings.sort((left, right) => left.sequenceNumber - right.sequenceNumber);
  }

  const rows: TaskRow[] = [];
  const visited = new Set<string>();

  function visit(parentId: string | null, depth: number): void {
    for (const task of childrenByParent.get(parentId) ?? []) {
      if (visited.has(task.id)) {
        continue;
      }

      visited.add(task.id);
      rows.push({ task, depth });
      visit(task.id, depth + 1);
    }
  }

  visit(null, 0);

  /*
    Việc nào không đi tới được từ gốc thì vẫn hiển thị, ở cấp ngoài cùng. Ẩn nó đi sẽ tạo ra một mẫu
    trông như thiếu việc mà không có gì báo — và số việc ở đầu trang sẽ không khớp với số dòng.
  */
  for (const task of tasks) {
    if (!visited.has(task.id)) {
      rows.push({ task, depth: 0 });
    }
  }

  return rows;
}

/**
 * Trang dựng cây công việc của một mẫu dự án.
 *
 * <b>Quan hệ phụ thuộc trong mẫu trỏ theo SỐ THỨ TỰ, không trỏ theo định danh.</b> Nhờ vậy nhìn vào
 * mẫu là đọc được ngay "việc 30 chờ việc 20", và một mẫu chép sang mẫu khác vẫn giữ nguyên quan hệ.
 * Số nhảy mười đơn vị mỗi lần để chèn được việc vào giữa mà không phải đánh số lại cả mẫu.
 *
 * <b>Việc tùy chọn là thứ làm mẫu dùng được cho nhiều dự án.</b> Dự án nào cũng cần thiết kế, nhưng
 * không dự án nào cũng cần hiệu chuẩn tại hiện trường. Đánh dấu tùy chọn thì lúc áp mẫu người lập kế
 * hoạch bỏ chọn được — còn việc bắt buộc thì không, vì bỏ một việc bắt buộc nghĩa là chọn sai mẫu.
 */
export function ProjectTemplateDetailPage(): ReactNode {
  const { templateId } = useParams<{ templateId: string }>();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canManage = hasPermission(PermissionCodes.ProjectTemplateManage);

  const [form] = Form.useForm<TaskFormValues>();

  const [detail, setDetail] = useState<ProjectTemplateDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [editing, setEditing] = useState<ProjectTemplateTask | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /*
    Danh sách kiểm giữ ở trạng thái riêng thay vì trong biểu mẫu: nó là một danh sách dòng thêm bớt
    được, và mỗi dòng có ba ô thuộc ba kiểu khác nhau. Để trong biểu mẫu thì phép kiểm của từng ô
    phải khai theo chỉ số dòng, và chỉ số dòng đổi mỗi lần xóa một dòng ở giữa.
  */
  const [checklistItems, setChecklistItems] = useState<readonly ProjectTemplateChecklistItem[]>([]);

  useEffect(() => {
    if (templateId === undefined) {
      return;
    }

    let isCurrent = true;

    async function load(identifier: string): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await getProjectTemplate(identifier);

        if (!isCurrent) {
          return;
        }

        setDetail(loaded);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(error instanceof ApiError ? error.message : 'Không tải được mẫu dự án.');
      }
    }

    void load(templateId);

    return () => {
      isCurrent = false;
    };
  }, [templateId, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openCreate(parentId: string | null): void {
    setEditing(null);
    setChecklistItems([]);
    form.resetFields();
    form.setFieldsValue({
      parentId,
      sequenceNumber: null,
      isMilestone: false,
      isOptional: false,
    });
    setIsFormOpen(true);
  }

  function openEdit(task: ProjectTemplateTask): void {
    setEditing(task);
    setChecklistItems(task.defaultChecklistItems);
    form.setFieldsValue({
      parentId: task.parentId,
      sequenceNumber: task.sequenceNumber,
      name: task.name,
      defaultDurationDays: task.defaultDurationDays,
      defaultEffortHours: task.defaultEffortHours,
      defaultAssigneeRole: task.defaultAssigneeRole,
      defaultProgressMethod: task.defaultProgressMethod,
      isMilestone: task.isMilestone,
      isOptional: task.isOptional,
      guidanceNote: task.guidanceNote,
      dependsOnSequenceNumbers: task.dependsOnSequenceNumbers,
    });
    setIsFormOpen(true);
  }

  async function submit(values: TaskFormValues): Promise<void> {
    if (templateId === undefined) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        parentId: values.parentId ?? null,

        // Bỏ trống số thứ tự nghĩa là đặt xuống cuối, và máy chủ nhận số 0 cho ý đó.
        sequenceNumber: values.sequenceNumber ?? 0,
        name: values.name,
        defaultDurationDays: values.defaultDurationDays ?? null,
        defaultEffortHours: values.defaultEffortHours ?? null,
        defaultAssigneeRole: values.defaultAssigneeRole ?? null,
        defaultProgressMethod: values.defaultProgressMethod ?? null,
        isMilestone: values.isMilestone,
        isOptional: values.isOptional,
        guidanceNote: values.guidanceNote ?? null,
        dependsOnSequenceNumbers: values.dependsOnSequenceNumbers ?? [],
        defaultChecklistItems: checklistItems.filter((item) => item.description.trim() !== ''),
      };

      if (editing === null) {
        await addProjectTemplateTask(templateId, payload);
        message.success('Đã thêm việc vào mẫu.');
      } else {
        await updateProjectTemplateTask(templateId, editing.id, payload);
        message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function removeTask(task: ProjectTemplateTask): Promise<void> {
    if (templateId === undefined) {
      return;
    }

    try {
      await removeProjectTemplateTask(templateId, task.id);

      message.success('Đã xóa việc khỏi mẫu.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  function addChecklistItem(): void {
    setChecklistItems((items) => [
      ...items,
      { description: '', isRequired: true, weight: defaultChecklistWeight },
    ]);
  }

  function changeChecklistItem(
    index: number,
    changes: Partial<ProjectTemplateChecklistItem>,
  ): void {
    setChecklistItems((items) =>
      items.map((item, current) => (current === index ? { ...item, ...changes } : item)),
    );
  }

  function removeChecklistItem(index: number): void {
    setChecklistItems((items) => items.filter((_, current) => current !== index));
  }

  if (errorMessage !== null && detail === null) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            void navigate(AppRoutes.ProjectTemplate);
          }}
        >
          Danh sách mẫu
        </Button>
        <Alert type="error" showIcon message={errorMessage} />
      </Space>
    );
  }

  if (detail === null || templateId === undefined) {
    return <Table<TaskRow> rowKey={(row) => row.task.id} loading size="small" />;
  }

  const rows = flattenTaskTree(detail.tasks);

  const columns: TableColumnsType<TaskRow> = [
    {
      title: 'STT',
      key: 'sequenceNumber',
      width: 80,
      align: 'right',
      render: (_: unknown, row) => <Typography.Text code>{row.task.sequenceNumber}</Typography.Text>,
    },
    {
      title: 'Tên việc',
      key: 'name',
      render: (_: unknown, row) => (
        <div style={{ paddingLeft: row.depth * indentWidth }}>
          <Space size={4} wrap>
            <span>{row.task.name}</span>
            {row.task.isMilestone ? <Tag color="gold">Mốc</Tag> : null}
            {row.task.isOptional ? <Tag color="blue">Tùy chọn</Tag> : null}
          </Space>
          {row.task.guidanceNote !== null && row.task.guidanceNote !== '' ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.task.guidanceNote}
              </Typography.Text>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Vai trò dự kiến',
      key: 'defaultAssigneeRole',
      width: 170,
      render: (_: unknown, row) =>
        row.task.defaultAssigneeRole === null
          ? '—'
          : projectMemberRoleNames[row.task.defaultAssigneeRole],
    },
    {
      title: 'Cách đo tiến độ',
      key: 'defaultProgressMethod',
      width: 170,
      render: (_: unknown, row) =>
        row.task.defaultProgressMethod === null
          ? '—'
          : projectTaskProgressMethodNames[row.task.defaultProgressMethod],
    },
    {
      title: 'Ngày',
      key: 'defaultDurationDays',
      width: 90,
      align: 'right',
      render: (_: unknown, row) => row.task.defaultDurationDays ?? '—',
    },
    {
      title: 'Giờ công',
      key: 'defaultEffortHours',
      width: 100,
      align: 'right',
      render: (_: unknown, row) => row.task.defaultEffortHours ?? '—',
    },
    {
      title: 'Chờ việc',
      key: 'dependsOn',
      width: 130,
      render: (_: unknown, row) =>
        row.task.dependsOnSequenceNumbers.length === 0 ? (
          '—'
        ) : (
          <Space size={2} wrap>
            {row.task.dependsOnSequenceNumbers.map((sequenceNumber) => (
              <Tag key={sequenceNumber}>{sequenceNumber}</Tag>
            ))}
          </Space>
        ),
    },
    {
      title: 'Mục kiểm',
      key: 'checklist',
      width: 110,
      align: 'right',
      render: (_: unknown, row) =>
        row.task.defaultChecklistItems.length === 0 ? (
          '—'
        ) : (
          <Tooltip
            title={row.task.defaultChecklistItems
              .map((item) => `${item.description}${item.isRequired ? ' (bắt buộc)' : ''}`)
              .join(' · ')}
          >
            <Tag color="green">{row.task.defaultChecklistItems.length}</Tag>
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: unknown, row) => (
        <Space size={2}>
          <Tooltip title="Thêm việc con">
            <Button
              size="small"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                openCreate(row.task.id);
              }}
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!canManage}
              onClick={() => {
                openEdit(row.task);
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa khỏi mẫu">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!canManage}
              onClick={() => {
                void removeTask(row.task);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /*
    Ô chọn việc cha loại chính việc đang sửa ra khỏi danh sách. Không loại các việc con của nó: máy
    chủ chưa có phép kiểm vòng cho cây trong mẫu, nên chỗ này chỉ chặn trường hợp hiển nhiên nhất là
    tự làm cha của chính mình.
  */
  const parentOptions = detail.tasks
    .filter((task) => task.id !== editing?.id)
    .map((task) => ({
      value: task.id,
      label: `${String(task.sequenceNumber)} — ${task.name}`,
    }));

  const dependencyOptions = detail.tasks
    .filter((task) => task.sequenceNumber !== editing?.sequenceNumber)
    .map((task) => ({
      value: task.sequenceNumber,
      label: `${String(task.sequenceNumber)} — ${task.name}`,
    }));

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Mẫu {detail.template.code} — v{detail.template.versionNumber}
          </Typography.Title>
          <Typography.Text type="secondary">{detail.template.name}</Typography.Text>
        </Space>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              void navigate(AppRoutes.ProjectTemplate);
            }}
          >
            Danh sách mẫu
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => {
              openCreate(null);
            }}
          >
            Thêm việc
          </Button>
        </Space>
      </Space>

      <Descriptions size="small" column={4} bordered>
        <Descriptions.Item label="Loại dự án">
          {detail.template.projectTypeName ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Số việc">{detail.tasks.length}</Descriptions.Item>
        <Descriptions.Item label="Thời lượng dự kiến">
          {detail.template.estimatedDurationMonths === null
            ? '—'
            : `${String(detail.template.estimatedDurationMonths)} tháng`}
        </Descriptions.Item>
        <Descriptions.Item label="Trạng thái">
          {detail.template.isActive ? (
            <Tag color="green">Còn dùng</Tag>
          ) : (
            <Tag>Đã ngừng dùng</Tag>
          )}
          {detail.template.isDefault ? <Tag color="gold">Mặc định</Tag> : null}
        </Descriptions.Item>
      </Descriptions>

      {errorMessage === null ? null : <Alert type="error" showIcon message={errorMessage} />}

      {detail.tasks.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Mẫu chưa có việc nào"
          description={
            'Dựng cây theo đúng cách công ty vẫn làm: nhóm việc lớn ở cấp ngoài, việc làm được trong '
            + 'vài ngày ở cấp trong. Việc nào không phải dự án nào cũng cần thì đánh dấu tùy chọn.'
          }
        />
      ) : null}

      <Table<TaskRow>
        rowKey={(row) => row.task.id}
        size="small"
        pagination={false}
        columns={columns}
        dataSource={[...rows]}
      />

      <Modal
        open={isFormOpen}
        width={720}
        title={editing === null ? 'Thêm việc vào mẫu' : `Sửa việc ${String(editing.sequenceNumber)}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        onOk={() => {
          form.submit();
        }}
      >
        <Form form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Form.Item
            name="name"
            label="Tên việc"
            extra={
              <span>
                Dùng được ba biến, thay lúc áp mẫu: <Typography.Text code>{`{${projectTemplateVariables.projectCode}}`}</Typography.Text>{' '}
                <Typography.Text code>{`{${projectTemplateVariables.projectName}}`}</Typography.Text>{' '}
                <Typography.Text code>{`{${projectTemplateVariables.customerName}}`}</Typography.Text>
              </span>
            }
            rules={[{ required: true, message: 'Nhập tên việc.' }, { max: 300 }]}
          >
            <Input placeholder="Thiết kế chiếu sáng cho {ten_khach_hang}" />
          </Form.Item>

          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Form.Item name="parentId" label="Việc cha" style={{ minWidth: 280 }}>
              <Select
                allowClear
                placeholder="Không có — việc ở cấp ngoài cùng"
                options={parentOptions}
              />
            </Form.Item>
            <Form.Item
              name="sequenceNumber"
              label="Số thứ tự"
              tooltip="Bỏ trống thì đặt xuống cuối. Số nhảy mười đơn vị để chèn được việc vào giữa."
              style={{ minWidth: 140 }}
            >
              <InputNumber min={1} step={10} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Form.Item
              name="defaultAssigneeRole"
              label="Vai trò dự kiến"
              tooltip="Chỉ để người lập kế hoạch biết nên giao cho ai. Áp mẫu KHÔNG tự giao việc cho người nào."
              style={{ minWidth: 220 }}
            >
              <Select
                allowClear
                placeholder="Chưa xác định"
                options={Object.values(ProjectMemberRole).map((role) => ({
                  value: role,
                  label: projectMemberRoleNames[role],
                }))}
              />
            </Form.Item>
            <Form.Item
              name="defaultProgressMethod"
              label="Cách đo tiến độ"
              tooltip="Chọn theo danh sách kiểm thì phải khai sẵn mục kiểm ở dưới, nếu không việc sinh ra sẽ không đo được."
              style={{ minWidth: 220 }}
            >
              <Select
                allowClear
                placeholder="Theo mặc định của việc mới"
                options={Object.values(ProjectTaskProgressMethod).map((method) => ({
                  value: method,
                  label: projectTaskProgressMethodNames[method],
                }))}
              />
            </Form.Item>
          </Space>

          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Form.Item name="defaultDurationDays" label="Thời lượng (ngày)" style={{ minWidth: 160 }}>
              <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="defaultEffortHours" label="Giờ công dự kiến" style={{ minWidth: 160 }}>
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="isMilestone"
              label="Việc mốc"
              valuePropName="checked"
              tooltip="Việc mốc là điểm nghiệm thu hoặc bàn giao — thứ mà người ngoài dự án cũng nhìn vào."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="isOptional"
              label="Tùy chọn"
              valuePropName="checked"
              tooltip="Việc tùy chọn bỏ chọn được lúc áp mẫu. Bỏ một nhóm thì bỏ theo cả nhánh bên dưới."
            >
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item
            name="dependsOnSequenceNumbers"
            label="Chờ những việc"
            tooltip="Trỏ theo số thứ tự. Đổi số thứ tự của một việc thì phải sửa lại những việc đang chờ nó."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Không chờ việc nào"
              options={dependencyOptions}
            />
          </Form.Item>

          <Form.Item name="guidanceNote" label="Hướng dẫn thực hiện" rules={[{ max: 4000 }]}>
            <Input.TextArea
              rows={3}
              placeholder="Những gì người làm cần biết mà không đọc được từ tên việc: tiêu chuẩn nghiệm thu, số liệu cần đạt, cạm bẫy hay gặp."
            />
          </Form.Item>

          <Typography.Text strong>Danh sách kiểm mặc định</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Áp mẫu sẽ tạo sẵn những mục này cho việc thật. Mục bắt buộc chặn việc chuyển sang hoàn
            thành khi chưa tích.
          </Typography.Paragraph>

          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {checklistItems.map((item, index) => (
              // Chỉ số dòng là khóa duy nhất ở đây: hai mục kiểm trùng nội dung là chuyện có thật,
              // và danh sách này chỉ tồn tại trong lúc cửa sổ mở.
              <Space key={index} align="start" wrap style={{ width: '100%' }}>
                <Input
                  style={{ minWidth: 320 }}
                  maxLength={500}
                  value={item.description}
                  placeholder="Nội dung mục kiểm"
                  onChange={(event) => {
                    changeChecklistItem(index, { description: event.target.value });
                  }}
                />
                <Tooltip title="Bắt buộc — chưa tích thì việc không được chuyển sang hoàn thành">
                  <Switch
                    checkedChildren="Bắt buộc"
                    unCheckedChildren="Tùy"
                    checked={item.isRequired}
                    onChange={(checked) => {
                      changeChecklistItem(index, { isRequired: checked });
                    }}
                  />
                </Tooltip>
                <Tooltip title="Trọng số khi tính phần trăm">
                  <InputNumber
                    min={0}
                    step={1}
                    style={{ width: 90 }}
                    value={item.weight}
                    onChange={(value) => {
                      changeChecklistItem(index, { weight: value ?? defaultChecklistWeight });
                    }}
                  />
                </Tooltip>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    removeChecklistItem(index);
                  }}
                />
              </Space>
            ))}
            <Button icon={<PlusOutlined />} onClick={addChecklistItem}>
              Thêm mục kiểm
            </Button>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
}
